import { useContext, useEffect, useState, type MutableRefObject, type ReactNode } from 'react';
import { UMButton, UMInputNumber } from '@urbanmetrix/um-kit';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import L from 'leaflet';
import { Select, TextInput, ViewContext } from 'components/lib';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { SimulationContext } from 'contexts/SimulationContext';
import CreateBorderButton from 'views/add-grid/cadastral/CreateBorderButton';
import { BUILDABLE_OFFSET_HINT, EDIT_DRAW_HINT, type Lv95Coordinate } from 'views/add-grid/cadastral/cadastralShared';
import Style from 'views/add-grid/styles/grid.tailwind';
import useAreaOffsetEditor from 'views/add-grid/cadastral/useAreaOffsetEditor';
import { DeleteIcon, EditIcon, MapSelectionIcon, OffsetIcon, SaveIcon } from 'components/icons';
import ProjectCreationSplitLayout from 'views/project-creation/ProjectCreationSplitLayout';
import { getShadowParameters } from './shadowParameters';
import {
  createNextResidentialArea,
  hasValidResidentialElevations,
  getResidentialAreas,
  getShadowSimulationsForVolumes,
  isShadowVolumeConfigured,
  type ResidentialArea,
} from './shadowResidentialAreas';
import 'views/add-geodata/styles/geodata.css';

type ShadowSection = 'normal' | 'residential' | 'parameters';
type ShadowDrawShape = 'polygon' | 'rectangle';
type ShadowAreaSource = 'property' | 'buildable' | 'analysis';

const Shadow = ({
  map,
  drawHintText,
  isNormalBuildableAreaEditing,
  onDrawNormalBuildableArea,
  onSelectNormalBuildableArea,
  onDeleteNormalBuildableArea,
  onEditNormalBuildableArea,
  normalBuildableAreaRef,
  isResidentialAreaEditing,
  onDrawResidentialArea,
  isResidentialParcelMode,
  isResidentialParcelModeUnavailable,
  residentialParcelSelectionCount,
  onToggleResidentialParcelMode,
  onSaveResidentialParcelSelection,
  onCancelResidentialParcelMode,
  onUpdateResidentialArea,
  onDeleteResidentialArea,
  onEditResidentialArea,
  residentialAreaRef,
  expandedResidentialAreaId,
  onExpandResidentialArea,
  hasPropertyArea,
  hasBuildableArea,
  hasAnalysisArea,
  analysisVolumeCount,
  selectedVolumeIndex,
  onSelectVolume,
}: {
  map: ReactNode;
  drawHintText: string;
  isNormalBuildableAreaEditing: boolean;
  onDrawNormalBuildableArea: (shapeType: ShadowDrawShape, subSimulationId: number) => void;
  onSelectNormalBuildableArea: (source: ShadowAreaSource, subSimulationId: number) => void;
  onDeleteNormalBuildableArea: (subSimulationId: number) => void;
  onEditNormalBuildableArea: (subSimulationId: number) => void;
  normalBuildableAreaRef: MutableRefObject<L.Polygon | null>;
  isResidentialAreaEditing: boolean;
  onDrawResidentialArea: (shapeType: ShadowDrawShape, subSimulationId: number, areaId: number) => void;
  isResidentialParcelMode: boolean;
  isResidentialParcelModeUnavailable: boolean;
  residentialParcelSelectionCount: number;
  onToggleResidentialParcelMode: (subSimulationId: number, areaId: number) => void;
  onSaveResidentialParcelSelection: () => void;
  onCancelResidentialParcelMode: () => void;
  onUpdateResidentialArea: (subSimulationId: number, areaId: number, coordinates: Lv95Coordinate[]) => boolean;
  onDeleteResidentialArea: (subSimulationId: number, areaId: number) => void;
  onEditResidentialArea: (subSimulationId: number, areaId: number) => void;
  residentialAreaRef: MutableRefObject<L.Polygon | null>;
  expandedResidentialAreaId: number | null;
  onExpandResidentialArea: (areaId: number | null) => void;
  hasPropertyArea: boolean;
  hasBuildableArea: boolean;
  hasAnalysisArea: boolean;
  analysisVolumeCount: number;
  selectedVolumeIndex: number;
  onSelectVolume: (volumeIndex: number) => void;
}) => {
  const [openSection, setOpenSection] = useState<ShadowSection | null>(null);
  const [normalAreaSourceModalOpen, setNormalAreaSourceModalOpen] = useState(false);
  const [normalAreaSource, setNormalAreaSource] = useState<ShadowAreaSource>('buildable');
  const viewContext = useContext(ViewContext);
  const { setSimulation, subSimulations, setSubSimulations, setSubSimulationParams, defaultShadow } =
    useContext(SimulationContext);
  const shadowSubSimulations = subSimulations.filter((subSimulation) => subSimulation.simType === 'SHADOW');
  const matchingShadowSubSimulations = shadowSubSimulations.filter(
    (subSimulation) => Number(subSimulation.params.analysisAreaIndex) === selectedVolumeIndex
  );
  const selectedShadowSubSimulations =
    matchingShadowSubSimulations.length > 0
      ? matchingShadowSubSimulations
      : shadowSubSimulations.filter((subSimulation) => subSimulation.params.analysisAreaIndex == null).slice(0, 1);
  const selectedShadowSubSimulation = selectedShadowSubSimulations[0];
  const normalBuildableArea = Array.isArray(selectedShadowSubSimulation?.params.normalBuildableArea)
    ? (selectedShadowSubSimulation.params.normalBuildableArea as Lv95Coordinate[])
    : [];

  const openNormalAreaSourceModal = () => {
    setNormalAreaSource(hasBuildableArea ? 'buildable' : hasPropertyArea ? 'property' : 'analysis');
    setNormalAreaSourceModalOpen(true);
  };

  const closeNormalAreaSourceModal = () => setNormalAreaSourceModalOpen(false);

  const offsetEditor = useAreaOffsetEditor({
    area: normalBuildableArea,
    areaLabel: 'normal buildable area',
    introTitle: 'Adjust normal buildable area offsets',
    inputName: 'normalBuildableAreaOffset',
    layerRef: normalBuildableAreaRef,
    onApply: (area) => {
      if (!selectedShadowSubSimulation) return false;
      setSubSimulationParams(selectedShadowSubSimulation.id, { normalBuildableArea: area });
      return true;
    },
    onError: (message) => viewContext.notification.show(message, 'error', true, 'toast'),
  });

  const toggleOffsetMode = () => {
    if (offsetEditor.isEngaged) {
      offsetEditor.interrupt();
      return;
    }
    if (normalBuildableArea.length < 3) return;
    if (isNormalBuildableAreaEditing && selectedShadowSubSimulation) {
      onEditNormalBuildableArea(selectedShadowSubSimulation.id);
    }
    offsetEditor.open();
  };

  const topLevelResidentialAreas = selectedShadowSubSimulation
    ? getResidentialAreas(selectedShadowSubSimulation.params)
    : [];
  const expandedResidentialArea = topLevelResidentialAreas.find((area) => area.id === expandedResidentialAreaId);
  const expandedResidentialAreaCoordinates = Array.isArray(expandedResidentialArea?.coordinates)
    ? (expandedResidentialArea.coordinates as Lv95Coordinate[])
    : [];

  const residentialOffsetEditor = useAreaOffsetEditor({
    area: expandedResidentialAreaCoordinates,
    areaLabel: 'residential area',
    introTitle: 'Adjust residential area offsets',
    inputName: 'residentialAreaOffset',
    layerRef: residentialAreaRef,
    onApply: (area) => {
      if (!selectedShadowSubSimulation || expandedResidentialAreaId == null) return false;
      return onUpdateResidentialArea(selectedShadowSubSimulation.id, expandedResidentialAreaId, area);
    },
    onError: (message) => viewContext.notification.show(message, 'error', true, 'toast'),
  });

  const toggleResidentialOffsetMode = () => {
    if (residentialOffsetEditor.isEngaged) {
      residentialOffsetEditor.interrupt();
      return;
    }
    if (expandedResidentialAreaCoordinates.length < 3 || expandedResidentialAreaId == null) return;
    if (isResidentialAreaEditing && selectedShadowSubSimulation) {
      onEditResidentialArea(selectedShadowSubSimulation.id, expandedResidentialAreaId);
    }
    residentialOffsetEditor.open();
  };

  const mapHintText =
    drawHintText ||
    (isResidentialParcelMode
      ? 'Select one or more parcels and Save. If not adjacent parcels are selected, more buildable areas will be created.'
      : offsetEditor.isEngaged || residentialOffsetEditor.isEngaged
        ? BUILDABLE_OFFSET_HINT
        : isNormalBuildableAreaEditing || isResidentialAreaEditing
          ? EDIT_DRAW_HINT
          : '');

  useEffect(() => {
    if (
      shadowSubSimulations.length === analysisVolumeCount &&
      shadowSubSimulations.every(
        (currentSubSimulation, index) => Number(currentSubSimulation.params.analysisAreaIndex) === index
      )
    ) {
      return;
    }

    setSubSimulations(getShadowSimulationsForVolumes(subSimulations, analysisVolumeCount, defaultShadow));
  }, [analysisVolumeCount, defaultShadow, setSubSimulations, shadowSubSimulations, subSimulations]);

  const sharedShadowName = shadowSubSimulations[0]?.name ?? '';
  const sharedShadowRule = String(shadowSubSimulations[0]?.params.shadowRule ?? 'ZH');

  const handleSharedNameChange = (_name: string, value: string) =>
    setSubSimulations(
      subSimulations.map((currentSubSimulation) =>
        currentSubSimulation.simType === 'SHADOW' ? { ...currentSubSimulation, name: value } : currentSubSimulation
      )
    );

  const handleSharedShadowRuleChange = (_name: string, value: string) => {
    setSubSimulations(
      subSimulations.map((currentSubSimulation) =>
        currentSubSimulation.simType === 'SHADOW'
          ? { ...currentSubSimulation, params: { ...currentSubSimulation.params, shadowRule: value } }
          : currentSubSimulation
      )
    );
    if (value === 'BS' && openSection === 'normal') setOpenSection(null);
  };

  const renderVolumeSelector = (isConfigured: boolean) => (
    <div className="shadow-volume-overview">
      {analysisVolumeCount > 1 && (
        <p className="shadow-volume-help">Configure one or more volumes independently. At least one is required.</p>
      )}
      <div className="analysis-area-volume-selector analysis-area-volume-selector--top shadow-volume-selector">
        {analysisVolumeCount > 1 && (
          <div className="analysis-area-volume-selector__pill">
            <button
              type="button"
              className="analysis-area-volume-selector__pill-nav"
              onClick={() => {
                offsetEditor.interrupt();
                onSelectVolume(selectedVolumeIndex - 1);
              }}
              aria-label="Previous analysis volume"
            >
              <ChevronLeftIcon fontSize="small" />
            </button>
            <span className="analysis-area-volume-selector__pill-label">{`Volume ${selectedVolumeIndex + 1}`}</span>
            <button
              type="button"
              className="analysis-area-volume-selector__pill-nav"
              onClick={() => {
                offsetEditor.interrupt();
                onSelectVolume(selectedVolumeIndex + 1);
              }}
              aria-label="Next analysis volume"
            >
              <ChevronRightIcon fontSize="small" />
            </button>
          </div>
        )}
        <span
          className={`shadow-volume-status shadow-volume-status--${isConfigured ? 'configured' : 'not-configured'}`}
        >
          {isConfigured ? 'Configured' : 'To be configured'}
        </span>
      </div>
      {!isConfigured && <p className="shadow-volume-help">Define the following parameters:</p>}
    </div>
  );

  return (
    <ProjectCreationSplitLayout
      className="grid-settings-layout shadow-simulation-layout"
      leftClassName="grid-settings-step__sidebar"
      rightClassName="grid-settings-layout__map"
      left={
        <>
          <div
            className="shadow-simulation-form shadow-simulation-shared-fields"
            aria-invalid={shadowSubSimulations.some((subSimulation) =>
              getResidentialAreas(subSimulation.params).some((area) => !hasValidResidentialElevations(area))
            )}
          >
            <TextInput label="Simulation Name" name="name" value={sharedShadowName} onChange={handleSharedNameChange} />
            <Select
              label="Shadow Rule"
              name="shadowRule"
              default="ZH"
              value={sharedShadowRule}
              options={[
                { value: 'ZH', label: 'ZH' },
                { value: 'BS', label: 'BS' },
              ]}
              style={{ cursor: 'pointer' }}
              onChange={handleSharedShadowRuleChange}
            />
          </div>
          {selectedShadowSubSimulations.map((subSimulation) => {
            const shadowRule = sharedShadowRule;
            const isNormalConditionsOpen = openSection === 'normal';
            const isResidentialAreasOpen = openSection === 'residential';
            const areParametersOpen = openSection === 'parameters';
            const parameters = getShadowParameters(shadowRule);
            const residentialAreas = getResidentialAreas(subSimulation.params);
            const isConfigured = isShadowVolumeConfigured(subSimulation.params);
            const hasNormalBuildableArea =
              Array.isArray(subSimulation.params.normalBuildableArea) &&
              subSimulation.params.normalBuildableArea.length >= 3;
            const updateResidentialArea = (areaId: number, changes: Partial<ResidentialArea>) => {
              setSubSimulationParams(subSimulation.id, {
                residentialAreas: residentialAreas.map((area) => (area.id === areaId ? { ...area, ...changes } : area)),
              });
            };

            return (
              <div key={subSimulation.id} className="shadow-simulation-form">
                {renderVolumeSelector(isConfigured)}
                {shadowRule === 'ZH' && (
                  <div className="geodata-accordion">
                    <button
                      type="button"
                      className={`geodata-accordion__trigger ${
                        isNormalConditionsOpen ? 'geodata-accordion__trigger--open' : ''
                      }`}
                      aria-expanded={isNormalConditionsOpen}
                      onClick={() => {
                        onCancelResidentialParcelMode();
                        setOpenSection(isNormalConditionsOpen ? null : 'normal');
                      }}
                    >
                      <span className="geodata-accordion__icon">{isNormalConditionsOpen ? '▾' : '▸'}</span>
                      <span className="geodata-slider-field__label--with-info">
                        <span className="shadow-accordion-label">
                          Normal Regulatory Conditions <span style={{ color: '#d32f2f' }}>*</span>
                        </span>
                        <CustomTooltip title="Normal construction rules of the surrounding neighborhood" />
                      </span>
                    </button>

                    {isNormalConditionsOpen && (
                      <div className="geodata-accordion__content shadow-regulatory-conditions shadow-area-accordion__content grid-analysis-step">
                        <div className="grid-border-section shadow-normal-buildable-area">
                          <div className="grid-border-section__main">
                            <div className="grid-border-section__label">
                              Normal Buildable Area <span style={{ color: '#d32f2f' }}>*</span>
                            </div>
                            <div className="grid-border-section__controls">
                              {hasNormalBuildableArea ? (
                                <div className="border-action-group">
                                  <div className="border-action-controls">
                                    <ButtonGroup variant="contained" color="success">
                                      <Button
                                        size="small"
                                        color="success"
                                        className={`border-action-icon-white ${offsetEditor.isActive ? 'save-button-active' : ''}`.trim()}
                                        onClick={toggleOffsetMode}
                                        aria-label="Offset Normal Buildable Area"
                                      >
                                        <OffsetIcon style={{ color: '#fff' }} />
                                      </Button>
                                      <Button
                                        size="small"
                                        color="success"
                                        className="border-action-icon-white"
                                        onClick={() => {
                                          offsetEditor.reset();
                                          onDeleteNormalBuildableArea(subSimulation.id);
                                        }}
                                        aria-label="Delete Normal Buildable Area"
                                      >
                                        <DeleteIcon style={{ color: '#fff' }} />
                                      </Button>
                                      <Button
                                        size="small"
                                        color="success"
                                        className={`border-action-icon-white ${
                                          isNormalBuildableAreaEditing ? 'save-button-active' : ''
                                        }`.trim()}
                                        onClick={() => {
                                          offsetEditor.reset();
                                          onEditNormalBuildableArea(subSimulation.id);
                                        }}
                                        aria-label={`${isNormalBuildableAreaEditing ? 'Save' : 'Edit'} Normal Buildable Area`}
                                      >
                                        {isNormalBuildableAreaEditing ? (
                                          <SaveIcon style={{ color: '#fff' }} />
                                        ) : (
                                          <EditIcon style={{ color: '#fff' }} />
                                        )}
                                      </Button>
                                    </ButtonGroup>
                                  </div>
                                </div>
                              ) : (
                                <CreateBorderButton
                                  color="success"
                                  startDrawing={() => undefined}
                                  borderType="analysisArea"
                                  btnText="Normal Buildable Area"
                                  showSelectButton={hasPropertyArea || hasBuildableArea || hasAnalysisArea}
                                  handleSelectClick={openNormalAreaSourceModal}
                                  onDrawRequest={(shapeType) => onDrawNormalBuildableArea(shapeType, subSimulation.id)}
                                  hideLabelChip
                                />
                              )}
                            </div>
                          </div>
                        </div>

                        <UMInputNumber
                          label={
                            <span className="grid-inline-label-with-info">
                              <span>Normal Maximal height [m]</span>
                              <CustomTooltip title="Maximum permitted building height under the normal regulatory conditions of the surrounding neighborhood." />
                            </span>
                          }
                          name="normalMaximalHeight"
                          value={Number(subSimulation.params.normalMaximalHeight ?? 9)}
                          defaultValue={9}
                          width={84}
                          onChange={(_name, value) =>
                            setSubSimulationParams(subSimulation.id, { normalMaximalHeight: value })
                          }
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="geodata-accordion">
                  <button
                    type="button"
                    className={`geodata-accordion__trigger ${
                      isResidentialAreasOpen ? 'geodata-accordion__trigger--open' : ''
                    }`}
                    aria-expanded={isResidentialAreasOpen}
                    onClick={() => {
                      onCancelResidentialParcelMode();
                      setOpenSection(isResidentialAreasOpen ? null : 'residential');
                    }}
                  >
                    <span className="geodata-accordion__icon">{isResidentialAreasOpen ? '▾' : '▸'}</span>
                    <span className="geodata-slider-field__label--with-info">
                      <span>
                        Buildable Residential Areas <span style={{ color: '#d32f2f' }}>*</span>
                      </span>
                      <CustomTooltip title="Buildable residential areas of the surrounding properties that could be impacted by the shadow of the high-rise building." />
                    </span>
                  </button>

                  {isResidentialAreasOpen && (
                    <div className="geodata-accordion__content shadow-regulatory-conditions shadow-residential-areas">
                      {residentialAreas.map((area, index) => {
                        const isAreaOpen = expandedResidentialAreaId === area.id;
                        const hasAreaCoordinates =
                          Array.isArray(area.coordinates) && (area.coordinates as Lv95Coordinate[]).length >= 3;
                        const isEditingThisArea = isAreaOpen && isResidentialAreaEditing;

                        return (
                          <div key={area.id} className="shadow-area-accordion">
                            <div className="shadow-area-accordion__header">
                              <button
                                type="button"
                                className="geodata-subaccordion__trigger"
                                aria-expanded={isAreaOpen}
                                onClick={() => {
                                  onCancelResidentialParcelMode();
                                  onExpandResidentialArea(isAreaOpen ? null : area.id);
                                }}
                              >
                                <span className="geodata-accordion__icon">{isAreaOpen ? '▾' : '▸'}</span>
                                <span>{`Area ${index + 1}`}</span>
                                <span
                                  aria-hidden="true"
                                  style={{ color: '#d32f2f', visibility: index === 0 ? 'visible' : 'hidden' }}
                                >
                                  *
                                </span>
                              </button>
                              {residentialAreas.length > 1 && (
                                <IconButton
                                  size="small"
                                  aria-label={`Delete Area ${index + 1}`}
                                  onClick={() => {
                                    onCancelResidentialParcelMode();
                                    if (isAreaOpen) {
                                      residentialOffsetEditor.reset();
                                      if (isResidentialAreaEditing) {
                                        onEditResidentialArea(subSimulation.id, area.id);
                                      }
                                    }
                                    setSubSimulationParams(subSimulation.id, {
                                      residentialAreas: residentialAreas.filter(
                                        (residentialArea) => residentialArea.id !== area.id
                                      ),
                                    });
                                    if (isAreaOpen) onExpandResidentialArea(null);
                                  }}
                                >
                                  <DeleteIcon style={{ width: '1rem', height: '1rem', color: '#000' }} />
                                </IconButton>
                              )}
                            </div>

                            {isAreaOpen && (
                              <div className="geodata-accordion__content shadow-area-accordion__content">
                                <div className="shadow-residential-area">
                                  {hasAreaCoordinates ? (
                                    <div className="border-action-group">
                                      <div className="border-action-controls">
                                        <ButtonGroup variant="contained" color="error">
                                          <Button
                                            size="small"
                                            color="error"
                                            className={`border-action-icon-white ${
                                              residentialOffsetEditor.isActive ? 'save-button-active' : ''
                                            }`.trim()}
                                            onClick={toggleResidentialOffsetMode}
                                            aria-label={`Offset Area ${index + 1}`}
                                          >
                                            <OffsetIcon style={{ color: '#fff' }} />
                                          </Button>
                                          <Button
                                            size="small"
                                            color="error"
                                            className="border-action-icon-white"
                                            onClick={() => {
                                              residentialOffsetEditor.reset();
                                              onDeleteResidentialArea(subSimulation.id, area.id);
                                            }}
                                            aria-label={`Delete Area ${index + 1}`}
                                          >
                                            <DeleteIcon style={{ color: '#fff' }} />
                                          </Button>
                                          <Button
                                            size="small"
                                            color="error"
                                            className={`border-action-icon-white ${
                                              isEditingThisArea ? 'save-button-active' : ''
                                            }`.trim()}
                                            onClick={() => {
                                              residentialOffsetEditor.reset();
                                              onEditResidentialArea(subSimulation.id, area.id);
                                            }}
                                            aria-label={`${isEditingThisArea ? 'Save' : 'Edit'} Area ${index + 1}`}
                                          >
                                            {isEditingThisArea ? (
                                              <SaveIcon style={{ color: '#fff' }} />
                                            ) : (
                                              <EditIcon style={{ color: '#fff' }} />
                                            )}
                                          </Button>
                                        </ButtonGroup>
                                      </div>
                                    </div>
                                  ) : (
                                    <CreateBorderButton
                                      color="error"
                                      startDrawing={() => undefined}
                                      borderType="analysisArea"
                                      btnText={`Area ${index + 1}`}
                                      showSelectButton
                                      handleSelectClick={() =>
                                        isResidentialParcelMode && residentialParcelSelectionCount > 0
                                          ? onSaveResidentialParcelSelection()
                                          : onToggleResidentialParcelMode(subSimulation.id, area.id)
                                      }
                                      isSelectActive={isResidentialParcelMode}
                                      isSelectDisabled={isResidentialParcelModeUnavailable}
                                      selectActionIcon={
                                        isResidentialParcelMode && residentialParcelSelectionCount > 0 ? (
                                          <SaveIcon style={{ color: '#fff' }} />
                                        ) : (
                                          <MapSelectionIcon style={{ color: '#fff' }} />
                                        )
                                      }
                                      onBeforeAction={onCancelResidentialParcelMode}
                                      onDrawRequest={(shapeType) =>
                                        onDrawResidentialArea(shapeType, subSimulation.id, area.id)
                                      }
                                      hideLabelChip
                                    />
                                  )}
                                </div>

                                <div className="shadow-area-accordion__row">
                                  <UMInputNumber
                                    label={
                                      <span>
                                        Height [m] <span style={{ color: '#d32f2f' }}>*</span>
                                      </span>
                                    }
                                    name={`residentialAreaMaxElevation-${area.id}`}
                                    value={area.maxElevation}
                                    defaultValue={10}
                                    valid={hasValidResidentialElevations(area)}
                                    errorMessage="Height must be greater than Elevation."
                                    width={84}
                                    onChange={(_name, value) => updateResidentialArea(area.id, { maxElevation: value })}
                                  />
                                  <UMInputNumber
                                    label={<span>Elevation [m]</span>}
                                    name={`residentialAreaMinElevation-${area.id}`}
                                    value={area.minElevation}
                                    defaultValue={0}
                                    valid={hasValidResidentialElevations(area)}
                                    errorMessage="Elevation must be less than Height."
                                    width={84}
                                    onChange={(_name, value) => updateResidentialArea(area.id, { minElevation: value })}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      <UMButton
                        text="Add Area"
                        className="glass-btn-1"
                        onClick={() => {
                          const newArea = createNextResidentialArea(residentialAreas);
                          if (isResidentialParcelMode && residentialParcelSelectionCount > 0) {
                            onSaveResidentialParcelSelection();
                          } else {
                            onCancelResidentialParcelMode();
                          }
                          setSimulation((currentSimulation) => ({
                            ...currentSimulation,
                            subSimulations: currentSimulation.subSimulations.map((currentSubSimulation) =>
                              currentSubSimulation.id === subSimulation.id
                                ? {
                                    ...currentSubSimulation,
                                    params: {
                                      ...currentSubSimulation.params,
                                      residentialAreas: [...getResidentialAreas(currentSubSimulation.params), newArea],
                                    },
                                  }
                                : currentSubSimulation
                            ),
                          }));
                          onExpandResidentialArea(newArea.id);
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="geodata-accordion shadow-show-parameters-accordion">
                  <button
                    type="button"
                    className={`geodata-accordion__trigger ${areParametersOpen ? 'geodata-accordion__trigger--open' : ''}`}
                    aria-expanded={areParametersOpen}
                    onClick={() => {
                      onCancelResidentialParcelMode();
                      setOpenSection(areParametersOpen ? null : 'parameters');
                    }}
                  >
                    <span className="geodata-accordion__icon">{areParametersOpen ? '▾' : '▸'}</span>
                    <span>Show Parameters</span>
                  </button>

                  {areParametersOpen && (
                    <div className="geodata-accordion__content shadow-show-parameters">
                      <div className="shadow-show-parameters__row">
                        <span className="shadow-show-parameters__label">Maximal shadow duration:</span>
                        <span>{parameters.maximalShadowDuration}</span>
                      </div>
                      <div className="shadow-show-parameters__row">
                        <span className="shadow-show-parameters__label">Reference Days:</span>
                        <span>{parameters.referenceDays}</span>
                      </div>
                      <div className="shadow-show-parameters__row">
                        <span className="shadow-show-parameters__label">Number of shadow segments:</span>
                        <span>{parameters.shadowSegments}</span>
                      </div>
                      <div className="shadow-show-parameters__row">
                        <span className="shadow-show-parameters__label">Hours and Angles:</span>
                        <table className="shadow-hours-table">
                          <thead>
                            <tr>
                              <th>Hour</th>
                              <th>Azimut [°]</th>
                              <th>Elevation [°]</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parameters.hoursAndAngles.map(({ hour, azimut, elevation }) => (
                              <tr key={hour}>
                                <td>{hour}</td>
                                <td>{azimut}</td>
                                <td>{elevation}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {shadowRule === 'ZH' && (
                        <>
                          <div className="shadow-show-parameters__note">
                            Exclude shadow allowed under normal regulatory conditions.
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <Modal open={normalAreaSourceModalOpen} onClose={closeNormalAreaSourceModal} className="custom-modal">
                  <Box sx={{ ...Style.modal, width: 'auto', border: 'none', borderRadius: '4px' }}>
                    <Typography variant="h6" component="h2">
                      Please choose one of the options.
                    </Typography>
                    <RadioGroup
                      value={normalAreaSource}
                      onChange={(event) => setNormalAreaSource(event.target.value as ShadowAreaSource)}
                      sx={{ mt: 1 }}
                    >
                      {hasPropertyArea && (
                        <FormControlLabel
                          className="radio-row"
                          value="property"
                          control={<Radio />}
                          label="The normal buildable area will be the same as the property area."
                        />
                      )}
                      {hasBuildableArea && (
                        <FormControlLabel
                          className="radio-row"
                          value="buildable"
                          control={<Radio />}
                          label="The normal buildable area will be the same as the buildable area."
                        />
                      )}
                      {hasAnalysisArea && (
                        <FormControlLabel
                          className="radio-row"
                          value="analysis"
                          control={<Radio />}
                          label="The normal buildable area will be the same as the analysis area."
                        />
                      )}
                    </RadioGroup>
                    <br />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                      <Button variant="contained" className="modal-no-btn" onClick={closeNormalAreaSourceModal}>
                        CANCEL
                      </Button>
                      <Button
                        variant="contained"
                        className="modal-yes-btn"
                        onClick={() => {
                          onSelectNormalBuildableArea(normalAreaSource, subSimulation.id);
                          closeNormalAreaSourceModal();
                        }}
                      >
                        Ok
                      </Button>
                    </Box>
                  </Box>
                </Modal>

                {offsetEditor.dialogs}
                {residentialOffsetEditor.dialogs}
              </div>
            );
          })}
        </>
      }
      right={map}
      rightFooter={
        <div className="grid-map-hint-slot" aria-live="polite">
          <p className="grid-map-hint grid-map-hint--visible">
            {mapHintText ||
              'Shadow diagram of the entire buildable volume that shows the area in which the maximum shadow time could be potentially exceeded. The buildable residential areas located inside the thick outline need to be defined.'}
          </p>
        </div>
      }
    />
  );
};

export default Shadow;
